import React from 'react';
import pluralize from 'pluralize';
import { Box, Typography, Theme, Collapse, IconButton, Chip } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Add, Loyalty, KeyboardArrowUp, KeyboardArrowDown } from '@material-ui/icons';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { TemplateSnackbar, SnackBarOptions } from '../../../../../common/snackbars/TemplateSnackbar';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ADMIN_CONSOLE_VOLUNTEER_TAG_QUERY } from './admin-console-volunteer-tags.gql';
import {
  GetAdminConsoleVolunteerTags,
  GetAdminConsoleVolunteerTagsVariables,
} from './__generated__/GetAdminConsoleVolunteerTags';
import { AdminConsoleVolunteerTag } from './__generated__/AdminConsoleVolunteerTag';
import { FormVolunteerTagDialog } from './FormVolunteerTagDialog';
import { VolunteerTagItem } from './VolunteerTagItem/VolunteerTagItem';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';

const UNGROUPED = '__ungrouped__';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '0.25rem',
    },
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      marginTop: '1.5rem',
      paddingBottom: '0.25rem',
      userSelect: 'none',
    },
    groupName: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.color.grey.neutral500,
    },
    groupCount: {
      height: 18,
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 500,
      color: theme.color.grey.neutral300,
      backgroundColor: '#f0f0f0',
      marginLeft: '8px',
    },
    emptyBody: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '1.5rem',
    },
  })
);

export function VolunteerTagsSection() {
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const { isSm } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [snackbar, setSnackBar] = React.useState<SnackBarOptions>();
  const [showTagDialog, setShowTagDialog] = React.useState(false);
  const [selectedTag, setSelectedTag] = React.useState<AdminConsoleVolunteerTag>();
  const [searchInput, setSearchInput] = React.useState<string>();
  const [collapsedGroups, setCollapsedGroups] = React.useState<string[]>([]);
  const [tagPendingDelete, setTagPendingDelete] = React.useState<AdminConsoleVolunteerTag>();

  const { data, refetch, loading } = useCampfireQuery<
    GetAdminConsoleVolunteerTags,
    GetAdminConsoleVolunteerTagsVariables
  >(ADMIN_CONSOLE_VOLUNTEER_TAG_QUERY, {
    options: { variables: { includeArchived: true } },
  });

  const volunteerTags = data?.vm.volunteerTags ?? [];

  const filteredTags = React.useMemo(
    () =>
      volunteerTags.filter((tag) => (searchInput ? tag.name.toLowerCase().includes(searchInput.toLowerCase()) : true)),
    [volunteerTags, searchInput]
  );

  const existingGroups = React.useMemo(() => {
    const groups: string[] = [];
    volunteerTags.forEach((tag) => {
      if (tag.tagGroup) {
        groups.push(tag.tagGroup);
      }
    });
    return Array.from(new Set(groups)).sort((a, b) => a.localeCompare(b));
  }, [volunteerTags]);

  const groupedTags = React.useMemo(() => {
    const groups = new Map<string, AdminConsoleVolunteerTag[]>();
    filteredTags.forEach((tag) => {
      const key = tag.tagGroup || UNGROUPED;
      groups.set(key, [...(groups.get(key) ?? []), tag]);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === UNGROUPED) return 1;
      if (b === UNGROUPED) return -1;
      return a.localeCompare(b);
    });
  }, [filteredTags]);

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => (prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]));
  };

  const handleCloseDialog = () => {
    setShowTagDialog(false);
    setSelectedTag(undefined);
  };

  const handleSuccess = (message: string) => {
    if (refetch) {
      setTimeout(refetch, 0);
    }
    handleCloseDialog();
    setSnackBar({ variant: 'success', open: true, message });
  };

  const onEdit = (tag: AdminConsoleVolunteerTag) => {
    setSelectedTag(tag);
    setShowTagDialog(true);
  };

  const onDelete = (tag: AdminConsoleVolunteerTag) => setTagPendingDelete(tag);

  const onConfirmDelete = () => {
    if (!tagPendingDelete) return;
    const { volunteerTagId } = tagPendingDelete;
    setTagPendingDelete(undefined);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/volunteer-tags/remove`,
        method: 'post',
        data: { volunteerTagId },
      })
      .then((res: any) => {
        if (!res?.ok) {
          setSnackBar({ variant: 'error', open: true, message: 'Unable to delete tag' });
          return;
        }
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackBar({ variant: 'success', open: true, message: 'Tag successfully removed' });
      })
      .catch(() => setSnackBar({ variant: 'error', open: true, message: 'Unable to delete tag' }));
  };

  return (
    <Box className={classes.contentContainer}>
      <Box
        display='flex'
        flexDirection={isSm ? 'column' : 'row'}
        alignItems={isSm ? 'left' : 'flex-start'}
        justifyContent='space-between'
      >
        <Box>
          <Typography className={classes.sectionTitle}>
            <Loyalty style={{ fontSize: '14px', paddingRight: '5px' }} /> User Tags
          </Typography>
          <Typography variant={'body2'} className={classes.sectionDescription}>
            {
              'User tags are a great way to label your volunteers with information that sits outside roles and programs, such as skills, qualifications, availability, and more.'
            }
          </Typography>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          style={{ gap: 12, marginLeft: isSm ? 0 : '0.75rem', paddingTop: isSm ? '1rem' : 0, flexShrink: 0 }}
        >
          <SearchField
            placeholder='Search user tags'
            onChange={(e: any) => setSearchInput(e.target.value)}
            fullWidth={isSm}
          />
          <ConsoleActionButton
            endIcon={<Add />}
            variant='contained'
            color='primary'
            onClick={() => setShowTagDialog(true)}
          >
            Add Tag
          </ConsoleActionButton>
        </Box>
      </Box>

      {volunteerTags.length ? (
        <Box marginTop='1.25rem'>
          {groupedTags.map(([group, tags], groupIndex) => (
            <Box key={group}>
              {group !== UNGROUPED && (
                <Box
                  className={classes.groupHeader}
                  style={{ marginTop: groupIndex === 0 ? 0 : '1.5rem' }}
                  onClick={() => toggleGroup(group)}
                >
                  <IconButton size='small' style={{ padding: 2, marginRight: 4 }}>
                    {collapsedGroups.includes(group) ? (
                      <KeyboardArrowDown fontSize='small' />
                    ) : (
                      <KeyboardArrowUp fontSize='small' />
                    )}
                  </IconButton>
                  <Typography className={classes.groupName}>{group}</Typography>
                  <Chip size='small' label={tags.length} className={classes.groupCount} />
                </Box>
              )}
              <Collapse in={group === UNGROUPED || !collapsedGroups.includes(group)} timeout='auto'>
                {tags.map((tag) => (
                  <VolunteerTagItem
                    key={tag.volunteerTagId}
                    tag={tag}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    isLoading={loading}
                  />
                ))}
              </Collapse>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant={'body2'} className={classes.emptyBody}>
          You haven’t created any user tags yet. Click the ‘<strong>Add Tag</strong>’ button to add your first.
        </Typography>
      )}

      {showTagDialog && (
        <FormVolunteerTagDialog
          tag={selectedTag}
          existingGroups={existingGroups}
          onClose={handleCloseDialog}
          onSuccess={handleSuccess}
        />
      )}
      <ConfirmationDialog
        open={!!tagPendingDelete}
        title={`Delete '${tagPendingDelete?.name ?? ''}'?`}
        body={
          tagPendingDelete && tagPendingDelete.volunteerCount > 0
            ? `This tag is on ${pluralize('volunteer', tagPendingDelete.volunteerCount, true)}. Deleting it removes ` +
              'it from all of them and erases the history of who had it, which cannot be undone. ' +
              'To stop it being added to anyone new while keeping it on current holders, edit the tag and archive it instead.'
            : 'This permanently deletes the tag and cannot be undone.'
        }
        closeActionText='Cancel'
        confirmActionText='Delete'
        handleCloseClick={() => setTagPendingDelete(undefined)}
        handleConfirmClick={onConfirmDelete}
      />
      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackBar(undefined)} />}
    </Box>
  );
}
